import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
    User,
    Users,
    Heart,
    ChevronRight,
    Home,
    Wind,
    Hand,
    Feather,
    HelpCircle,
    BookOpen
} from 'lucide-react';
import { PrimaryButton, SecondaryButton, TertiaryButton } from '../components/brand/BrandButton';
import { BrandCard } from '../components/brand/BrandCard';
import { Heading, BodyText, SmallText } from '../components/brand/BrandTypography';
import { IconContainer } from '../components/brand/BrandIcon';
import { BoxBreathingVisual } from '../components/brand/BoxBreathingVisual';
import { ModuleHeader, SafetyFooter } from '../components/brand/QuickExit';
import { PersistentBottomBar, BottomBarSpacer } from '../components/brand/PersistentBottomBar';
import { CountdownRing } from '../components/brand/CountdownRing';
import { EmotionReferenceGuide, EMOTION_DATA } from '../components/cycle/EmotionReferenceGuide';

const SoloWhatDoINeed = () => {
    const navigate = useNavigate();
    const [step, setStep] = useState('intro'); // intro, body, emotion, emotion-deep, need, guidance, regulation, closing
    const [bodyState, setBodyState] = useState(null);
    const [emotion, setEmotion] = useState(null);
    const [selectedEmotions, setSelectedEmotions] = useState([]);
    const [expandedEmotion, setExpandedEmotion] = useState(null);
    const [useDeepEmotions, setUseDeepEmotions] = useState(false);
    const [need, setNeed] = useState(null);
    const [regulationChoice, setRegulationChoice] = useState(null);
    const [ringKey, setRingKey] = useState(0); // Key to reset countdown ring

    // Handle deep emotion selection for emotion-deep step
    const handleDeepEmotionSelect = (emotionId) => {
        setSelectedEmotions(prev => {
            if (prev.includes(emotionId)) {
                return prev.filter(id => id !== emotionId);
            }
            if (prev.length >= 2) {
                return [...prev.slice(1), emotionId];
            }
            return [...prev, emotionId];
        });
    };

    // Get selected emotion details
    const selectedEmotionDetails = selectedEmotions.map(id => 
        EMOTION_DATA.find(e => e.id === id)
    ).filter(Boolean);

    // Derive needs from selected emotions
    const derivedNeeds = [...new Set(selectedEmotionDetails.flatMap(e => 
        e.underlyingNeed.split(', ').slice(0, 2)
    ))].slice(0, 3);

    // Body options - added "Achy or hurting"
    const bodyOptions = [
        { id: 'tense', label: 'Tense or tight' },
        { id: 'heavy', label: 'Heavy or slow' },
        { id: 'achy', label: 'Achy or hurting' },
        { id: 'hollow', label: 'Hollow or empty' }
    ];

    // Emotion options - added "Hurt"
    const emotionOptions = [
        { id: 'alone', label: 'Alone' },
        { id: 'hurt', label: 'Hurt' },
        { id: 'small', label: 'Small' },
        { id: 'unseen', label: 'Unseen' }
    ];

    // Need options - added warmth-focused options
    const needOptions = [
        { id: 'comfort', label: 'Some comfort' },
        { id: 'seen', label: 'To feel seen' },
        { id: 'connection', label: 'To feel less alone' },
        { id: 'kindness', label: 'Some kindness' }
    ];

    // Validation messages based on emotion selection
    const getEmotionValidation = () => {
        // If using deep emotions, get validation from selected emotion data
        if (useDeepEmotions && selectedEmotionDetails.length > 0) {
            const firstEmotion = selectedEmotionDetails[0];
            return `${firstEmotion.name} is real and valid. You deserve to be held gently.`;
        }
        
        const validations = {
            alone: "Loneliness can feel so heavy. You came here anyway. That matters.",
            hurt: "Hurt is real. It deserves to be held gently — including yours.",
            small: "Feeling small doesn't mean you are small. You matter.",
            unseen: "Wanting to be seen is deeply human. You deserve that.",
            unsure: "It's okay not to have words for it. You're still welcome here."
        };
        return validations[emotion] || validations['unsure'];
    };

    // Guidance based on need - warmer, more holding
    const getGuidance = () => {
        const guidanceMap = {
            comfort: {
                text: 'You deserve comfort right now.',
                suggestion: 'Place both hands on your heart. Feel the warmth of your own touch. Whisper: "I\'m here with you."',
                closing: 'This tenderness is for you. Let it move through you.'
            },
            seen: {
                text: 'You\'re here. I see you.',
                suggestion: 'Whatever you\'re carrying — even if no one else knows — it\'s real. And it matters.',
                closing: 'You don\'t have to carry it alone.'
            },
            connection: {
                text: 'You\'re reaching for connection.',
                suggestion: 'That takes courage, especially when you\'re hurting. Even being here is a form of reaching out.',
                closing: 'You\'re not as alone as you feel right now.'
            },
            kindness: {
                text: 'You deserve kindness.',
                suggestion: 'Speak to yourself the way you\'d speak to someone you love who was hurting.',
                closing: 'Be gentle with yourself right now. You\'re doing your best.'
            },
            unsure: {
                text: 'That\'s okay.',
                suggestion: 'Sometimes just pausing is enough. You don\'t need to figure it all out.',
                closing: 'You showed up for yourself. That matters more than you know.'
            }
        };
        return guidanceMap[need] || guidanceMap['unsure'];
    };

    // Regulation options - warmer labels with durations
    const regulationOptions = [
        { 
            id: 'breath', 
            label: 'A gentle breath',
            icon: Wind,
            instruction: 'Breathe in softly... and let it go.',
            hasBreath: true,
            duration: 11
        },
        { 
            id: 'hold', 
            label: 'Hold yourself',
            icon: Hand,
            instruction: 'Wrap your arms around yourself. You\'re here. You\'re okay.',
            hasBreath: false,
            duration: 10
        },
        { 
            id: 'ground', 
            label: 'Feel the ground',
            icon: Feather,
            instruction: 'Press your feet down. The earth is holding you.',
            hasBreath: false,
            duration: 10
        }
    ];

    // Get closing message based on emotion
    const getClosingMessage = () => {
        // Specific message for hurt AND alone combination
        if ((emotion === 'hurt' || emotion === 'alone') && (need === 'connection' || need === 'comfort')) {
            return {
                main: "You're not alone in this.",
                sub: "Notice this feeling as it rises, peaks, and softens. You matter, even when it hurts."
            };
        }
        if (emotion === 'alone' || need === 'connection') {
            return {
                main: "You're not alone.",
                sub: "Even in this moment, reaching here means something. You matter."
            };
        }
        if (emotion === 'hurt' || need === 'comfort') {
            return {
                main: "Your heart is tender right now.",
                sub: "Let this feeling shift and change however it wants to. Be gentle with yourself."
            };
        }
        if (emotion === 'small' || emotion === 'unseen') {
            return {
                main: "You were seen here.",
                sub: "What you feel is real. And it matters deeply."
            };
        }
        return {
            main: "You showed up for yourself.",
            sub: "That's more than enough."
        };
    };

    // Selection handlers
    const handleBodySelect = (id) => {
        setBodyState(id);
        setStep('emotion');
    };

    const handleEmotionSelect = (id) => {
        setEmotion(id);
        setStep('need');
    };

    const handleNeedSelect = (id) => {
        setNeed(id);
        setStep('guidance');
    };

    const handleBackNavigation = () => {
        if (step === 'intro') {
            navigate('/relationship-hub');
        } else if (step === 'body') {
            setStep('intro');
        } else if (step === 'emotion') {
            setStep('body');
        } else if (step === 'emotion-deep') {
            setStep('emotion');
            setSelectedEmotions([]);
        } else if (step === 'need') {
            if (useDeepEmotions) {
                setStep('emotion-deep');
            } else {
                setStep('emotion');
            }
        } else if (step === 'guidance') {
            setStep('need');
        } else if (step === 'regulation') {
            if (regulationChoice) {
                setRegulationChoice(null);
            } else {
                setStep('guidance');
            }
        }
    };

    // Simple option button component
    const OptionButton = ({ label, isSelected, onClick, index }) => (
        <button
            onClick={onClick}
            className={`w-full p-4 rounded-2xl text-left border-2 transition-all duration-200 animate-fade-in
                ${isSelected 
                    ? 'border-[#1D6B6B] bg-[var(--orenda-soft-teal)]/10' 
                    : 'border-[var(--orenda-border)] bg-[var(--orenda-soft-white)] hover:bg-[var(--orenda-cream)] hover:border-[var(--orenda-border-hover)]'
                }`}
            style={{ animationDelay: `${index * 60}ms` }}
        >
            <BodyText className="text-center">{label}</BodyText>
        </button>
    );

    return (
        <div className="min-h-screen bg-transparent">
            {/* Orenda Module Header */}
            <ModuleHeader
                title="Check In"
                onBack={handleBackNavigation}
                backDestination="/relationship-hub"
                exitDestination="/home?tab=relationships"
                showBack={step !== 'closing'}
            />

            <main className="max-w-md mx-auto px-6 py-8">

                {/* INTRO - Warmer, meets them where they are */}
                {step === 'intro' && (
                    <div className="animate-fade-in">
                        <div className="text-center mb-10">
                            <IconContainer variant="rose" size="xl" useGradient={true} className="mx-auto mb-6">
                                <Heart className="w-8 h-8" />
                            </IconContainer>
                            
                            <BodyText className="text-[var(--orenda-text-secondary)] leading-relaxed mb-3">
                                You're here.
                            </BodyText>
                            
                            <SmallText className="text-[var(--orenda-text-muted)] leading-relaxed mb-2">
                                Whatever you're carrying — hurt, loneliness, confusion —
                            </SmallText>
                            <SmallText className="text-[var(--orenda-text-muted)] leading-relaxed">
                                it's welcome here.
                            </SmallText>
                        </div>
                        
                        <div className="mt-8 text-center">
                            <SmallText className="text-[var(--orenda-text-muted)] italic">
                                Take your time. There's no rush.
                            </SmallText>
                        </div>
                        
                        {/* Bottom spacer */}
                        <BottomBarSpacer height="default" />
                    </div>
                )}
                
                {/* Persistent Bottom Bar for Intro */}
                {step === 'intro' && (
                    <PersistentBottomBar>
                        <PrimaryButton
                            onClick={() => setStep('body')}
                            className="w-full"
                            size="action"
                            data-testid="start-btn"
                        >
                            Okay
                            <ChevronRight className="w-4 h-4 ml-1.5" />
                        </PrimaryButton>
                    </PersistentBottomBar>
                )}

                {/* STEP 1: BODY - With "achy/hurting" option */}
                {step === 'body' && (
                    <div className="animate-fade-in">
                        <div className="text-center mb-8">
                            <BodyText className="text-[var(--orenda-text-secondary)]">
                                How does your body feel?
                            </BodyText>
                        </div>

                        <div className="space-y-3 mb-4">
                            {bodyOptions.map((option, index) => (
                                <OptionButton
                                    key={option.id}
                                    label={option.label}
                                    isSelected={bodyState === option.id}
                                    onClick={() => handleBodySelect(option.id)}
                                    index={index}
                                />
                            ))}
                            
                            <button
                                onClick={() => handleBodySelect('unsure')}
                                className="w-full p-4 rounded-2xl text-center border-2 border-dashed 
                                           border-[var(--orenda-border)] bg-transparent
                                           hover:bg-[var(--orenda-cream)] hover:border-[var(--orenda-border-hover)] 
                                           transition-all duration-200 animate-fade-in"
                                style={{ animationDelay: '240ms' }}
                            >
                                <div className="flex items-center justify-center gap-2">
                                    <HelpCircle className="w-4 h-4 text-[var(--orenda-text-muted)]" />
                                    <SmallText className="text-[var(--orenda-text-muted)]">
                                        I'm not sure
                                    </SmallText>
                                </div>
                            </button>
                        </div>

                        <div className="text-center">
                            <SmallText className="text-[var(--orenda-text-muted)] italic">
                                There's no wrong answer.
                            </SmallText>
                        </div>

                        <SafetyFooter
                            destination="/relationship-hub"
                            exitLabel="Come back later"
                            reassurance=""
                        />
                    </div>
                )}

                {/* STEP 2: EMOTION - With "Hurt" option and validation */}
                {step === 'emotion' && (
                    <div className="animate-fade-in">
                        <div className="text-center mb-8">
                            <BodyText className="text-[var(--orenda-text-secondary)]">
                                What word fits right now?
                            </BodyText>
                        </div>

                        <div className="space-y-3 mb-4">
                            {emotionOptions.map((option, index) => (
                                <OptionButton
                                    key={option.id}
                                    label={option.label}
                                    isSelected={emotion === option.id}
                                    onClick={() => handleEmotionSelect(option.id)}
                                    index={index}
                                />
                            ))}
                            
                            <button
                                onClick={() => handleEmotionSelect('unsure')}
                                className="w-full p-4 rounded-2xl text-center border-2 border-dashed 
                                           border-[var(--orenda-border)] bg-transparent
                                           hover:bg-[var(--orenda-cream)] hover:border-[var(--orenda-border-hover)] 
                                           transition-all duration-200 animate-fade-in"
                                style={{ animationDelay: '240ms' }}
                            >
                                <div className="flex items-center justify-center gap-2">
                                    <HelpCircle className="w-4 h-4 text-[var(--orenda-text-muted)]" />
                                    <SmallText className="text-[var(--orenda-text-muted)]">
                                        I don't know
                                    </SmallText>
                                </div>
                            </button>
                        </div>

                        {/* Go Deeper option */}
                        <div className="mt-6 pt-4 border-t border-[var(--orenda-border)]/50">
                            <button
                                onClick={() => {
                                    setUseDeepEmotions(true);
                                    setStep('emotion-deep');
                                }}
                                className="w-full p-4 rounded-2xl text-center bg-[#7D8E95]/10 hover:bg-[#7D8E95]/20 
                                           border border-[#7D8E95]/30 transition-all duration-200"
                                data-testid="go-deeper-btn"
                            >
                                <div className="flex items-center justify-center gap-2">
                                    <BookOpen className="w-4 h-4 text-[#7D8E95]" />
                                    <SmallText className="text-[#7D8E95] font-medium">
                                        Explore emotions more deeply
                                    </SmallText>
                                </div>
                            </button>
                        </div>

                        <div className="text-center mt-4">
                            <SmallText className="text-[var(--orenda-text-muted)] italic">
                                Just pick what feels closest.
                            </SmallText>
                        </div>

                        <SafetyFooter
                            destination="/relationship-hub"
                            exitLabel="Come back later"
                            reassurance=""
                        />
                    </div>
                )}

                {/* STEP 2B: DEEP EMOTION - Full Emotion Reference Guide */}
                {step === 'emotion-deep' && (
                    <div className="animate-fade-in">
                        <div className="text-center mb-6">
                            <IconContainer variant="lavender" size="lg" useGradient={true} className="mx-auto mb-4">
                                <BookOpen className="w-6 h-6" />
                            </IconContainer>
                            <Heading as="h2" size="text-xl" className="mb-2">
                                What's happening inside?
                            </Heading>
                            <BodyText className="text-[var(--orenda-text-secondary)]">
                                Select up to 2 emotions that feel familiar right now.
                            </BodyText>
                        </div>

                        <EmotionReferenceGuide
                            selectedEmotions={selectedEmotions}
                            onEmotionSelect={handleDeepEmotionSelect}
                            expandedEmotion={expandedEmotion}
                            onExpandEmotion={setExpandedEmotion}
                            className="mb-6"
                        />

                        {selectedEmotions.length > 0 && (
                            <div className="glass-card rounded-2xl p-5">
                                <SmallText className="text-[#7D8E95] font-medium block mb-3">
                                    What you might need:
                                </SmallText>
                                <div className="space-y-2">
                                    {derivedNeeds.map((need, i) => (
                                        <div key={i} className="flex items-start gap-2">
                                            <div className="w-1.5 h-1.5 rounded-full bg-[var(--orenda-soft-teal)] mt-2" />
                                            <SmallText className="text-[var(--orenda-text-primary)]">
                                                {need}
                                            </SmallText>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        <BottomBarSpacer height="default" />
                    </div>
                )}

                {step === 'emotion-deep' && (
                    <PersistentBottomBar>
                        <div className="flex gap-3 w-full">
                            <SecondaryButton
                                onClick={() => {
                                    setStep('emotion');
                                    setSelectedEmotions([]);
                                }}
                                className="flex-1"
                                size="action"
                            >
                                Back
                            </SecondaryButton>
                            <PrimaryButton
                                onClick={() => setStep('need')}
                                className="flex-1"
                                size="action"
                                disabled={selectedEmotions.length === 0}
                            >
                                Continue
                                <ChevronRight className="w-4 h-4 ml-1" />
                            </PrimaryButton>
                        </div>
                    </PersistentBottomBar>
                )}

                {/* STEP 3: NEED - With warmth options and validation */}
                {step === 'need' && (
                    <div className="animate-fade-in">
                        {/* Validation message based on emotion */}
                        <div className="glass-card rounded-2xl p-5">
                            <SmallText className="text-[var(--orenda-text-secondary)] text-center italic leading-relaxed">
                                {getEmotionValidation()}
                            </SmallText>
                        </div>

                        <div className="text-center mb-6">
                            <BodyText className="text-[var(--orenda-text-secondary)]">
                                What would help right now?
                            </BodyText>
                        </div>

                        <div className="space-y-3 mb-4">
                            {needOptions.map((option, index) => (
                                <OptionButton
                                    key={option.id}
                                    label={option.label}
                                    isSelected={need === option.id}
                                    onClick={() => handleNeedSelect(option.id)}
                                    index={index}
                                />
                            ))}
                            
                            <button
                                onClick={() => handleNeedSelect('unsure')}
                                className="w-full p-4 rounded-2xl text-center border-2 border-dashed 
                                           border-[var(--orenda-border)] bg-transparent
                                           hover:bg-[var(--orenda-cream)] hover:border-[var(--orenda-border-hover)] 
                                           transition-all duration-200 animate-fade-in"
                                style={{ animationDelay: '240ms' }}
                            >
                                <div className="flex items-center justify-center gap-2">
                                    <HelpCircle className="w-4 h-4 text-[var(--orenda-text-muted)]" />
                                    <SmallText className="text-[var(--orenda-text-muted)]">
                                        I'm not sure
                                    </SmallText>
                                </div>
                            </button>
                        </div>

                        <SafetyFooter
                            destination="/relationship-hub"
                            exitLabel="Come back later"
                            reassurance=""
                        />
                    </div>
                )}

                {/* STEP 4: GUIDANCE - Warmer, more holding */}
                {step === 'guidance' && (
                    <div className="animate-fade-in">
                        <div className="text-center mb-6">
                            <IconContainer variant="rose" size="lg" useGradient={true} className="mx-auto mb-4">
                                <Heart className="w-5 h-5" />
                            </IconContainer>
                        </div>

                        <div className="glass-card rounded-2xl p-5 mb-6">
                            <div className="text-center py-2">
                                <BodyText className="text-[var(--orenda-text-primary)] leading-relaxed mb-4">
                                    {getGuidance().text}
                                </BodyText>
                                <div className="p-4 rounded-xl bg-[var(--orenda-blush-rose)]/10 border border-[var(--orenda-blush-rose)]/15 mb-4">
                                    <SmallText className="text-[var(--orenda-text-secondary)] italic leading-relaxed">
                                        {getGuidance().suggestion}
                                    </SmallText>
                                </div>
                                <SmallText className="text-[var(--orenda-text-muted)]">
                                    {getGuidance().closing}
                                </SmallText>
                            </div>
                        </div>

                        <BottomBarSpacer height="extended" />
                    </div>
                )}
                
                {/* Persistent Bottom Bar for Guidance */}
                {step === 'guidance' && (
                    <PersistentBottomBar>
                        <PrimaryButton
                            onClick={() => setStep('regulation')}
                            className="w-full"
                            size="action"
                        >
                            Continue
                            <ChevronRight className="w-4 h-4 ml-1.5" />
                        </PrimaryButton>
                        
                        <TertiaryButton
                            onClick={() => setStep('closing')}
                            className="w-full text-[var(--orenda-text-muted)] text-sm"
                        >
                            That's enough for now
                        </TertiaryButton>
                    </PersistentBottomBar>
                )}

                {/* STEP 5: REGULATION - Warmer options */}
                {step === 'regulation' && !regulationChoice && (
                    <div className="animate-fade-in">
                        <div className="text-center mb-8">
                            <BodyText className="text-[var(--orenda-text-secondary)]">
                                One gentle moment?
                            </BodyText>
                            <SmallText className="text-[var(--orenda-text-muted)] mt-1">
                                Or skip — that's okay too.
                            </SmallText>
                        </div>

                        <div className="space-y-3 mb-6">
                            {regulationOptions.map((option, index) => (
                                <button
                                    key={option.id}
                                    onClick={() => setRegulationChoice(option)}
                                    className="w-full p-4 rounded-2xl text-left border-2 border-[var(--orenda-border)] 
                                               bg-[var(--orenda-soft-white)] hover:bg-[var(--orenda-cream)] 
                                               hover:border-[var(--orenda-border-hover)] transition-all duration-200
                                               animate-fade-in"
                                    style={{ animationDelay: `${index * 60}ms` }}
                                    data-testid={`regulation-${option.id}`}
                                >
                                    <div className="flex items-center gap-4">
                                        <div className="w-10 h-10 rounded-full bg-[var(--orenda-blush-rose)]/20 flex items-center justify-center flex-shrink-0">
                                            <option.icon className="w-5 h-5 text-[#FFBB98]" />
                                        </div>
                                        <BodyText>{option.label}</BodyText>
                                    </div>
                                </button>
                            ))}
                        </div>

                        <TertiaryButton
                            onClick={() => setStep('closing')}
                            className="w-full text-[var(--orenda-text-muted)]"
                        >
                            Skip
                        </TertiaryButton>
                    </div>
                )}

                {/* STEP 5: REGULATION - Active */}
                {step === 'regulation' && regulationChoice && (
                    <div className="animate-fade-in">
                        <div className="text-center mb-6">
                            <IconContainer variant="rose" size="lg" useGradient={true} className="mx-auto mb-4">
                                <regulationChoice.icon className="w-5 h-5" />
                            </IconContainer>
                        </div>

                        <div className="glass-card rounded-2xl p-5">
                            <div className="text-center py-4">
                                <BodyText className="text-[var(--orenda-text-primary)] leading-relaxed text-lg mb-6">
                                    {regulationChoice.instruction}
                                </BodyText>
                                
                                {regulationChoice.hasBreath && (
                                    <div className="flex justify-center mt-4">
                                        <BoxBreathingVisual 
                                            autoStart={true} 
                                            size="md"
                                            variant="sage"
                                            showControls={false}
                                            showPhaseIndicators={true}
                                        />
                                    </div>
                                )}
                                
                                {/* Countdown Ring - for non-breath regulations */}
                                {!regulationChoice.hasBreath && regulationChoice.duration > 0 && (
                                    <div className="flex justify-center mt-6">
                                        <CountdownRing
                                            key={ringKey}
                                            duration={regulationChoice.duration}
                                            size="md"
                                            variant="lavender"
                                            autoStart={true}
                                            showOnComplete={true}
                                        />
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Bottom spacer - breathwork for animation clearance */}
                        <BottomBarSpacer height="breathwork" />
                    </div>
                )}
                
                {/* Persistent Bottom Bar for Regulation Active */}
                {step === 'regulation' && regulationChoice && (
                    <PersistentBottomBar>
                        <PrimaryButton
                            onClick={() => setStep('closing')}
                            className="w-full"
                            size="action"
                        >
                            Done
                        </PrimaryButton>
                    </PersistentBottomBar>
                )}

                {/* STEP 6: CLOSING - Warm, acknowledges their specific feeling */}
                {step === 'closing' && (
                    <div className="animate-fade-in">
                        {/* Mark solo tool as completed */}
                        {(() => {
                            localStorage.setItem('solo_tool_completed', 'true');
                            localStorage.setItem('solo_tool_last_need', need || '');
                            localStorage.setItem('solo_tool_last_emotion', emotion || '');
                            return null;
                        })()}
                        
                        <div className="text-center mb-6">
                            <IconContainer variant="rose" size="xl" useGradient={true} className="mx-auto mb-4">
                                <Heart className="w-6 h-6" />
                            </IconContainer>
                        </div>

                        <div className="text-center mb-8">
                            <Heading as="h2" size="text-xl" className="mb-2">
                                {getClosingMessage().main}
                            </Heading>
                            <SmallText className="text-[var(--orenda-text-muted)] leading-relaxed">
                                {getClosingMessage().sub}
                            </SmallText>
                        </div>

                        {/* Partner Mode suggestion - if feeling alone, hurt, or seeking connection */}
                        {(emotion === 'alone' || emotion === 'hurt' || need === 'connection' || need === 'comfort') && (
                            <div className="glass-card rounded-2xl p-5">
                                <BodyText className="text-[var(--orenda-text-secondary)] text-center text-sm leading-relaxed mb-3">
                                    {emotion === 'hurt' || need === 'comfort' 
                                        ? "When you're ready, connection can help. Partner Mode is here if you want it."
                                        : "If you'd like to connect with your partner, Partner Mode can help."
                                    }
                                </BodyText>
                                <div className="flex gap-2">
                                    <SecondaryButton
                                        onClick={() => navigate('/regulate/partner-mode')}
                                        className="flex-1 text-sm"
                                        data-testid="open-partner-mode-btn"
                                    >
                                        <Users className="w-4 h-4 mr-1" />
                                        Partner Mode
                                    </SecondaryButton>
                                    <TertiaryButton className="flex-1 text-[var(--orenda-text-muted)] text-sm">
                                        Not now
                                    </TertiaryButton>
                                </div>
                            </div>
                        )}

                        <BottomBarSpacer height="extended" />
                    </div>
                )}
                
                {/* Persistent Bottom Bar for Closing */}
                {step === 'closing' && (
                    <PersistentBottomBar>
                        <PrimaryButton
                            onClick={() => navigate('/relationship-hub')}
                            className="w-full"
                            size="action"
                            data-testid="return-to-hub-btn"
                        >
                            <Home className="w-4 h-4 mr-1.5" />
                            Return to Hub
                        </PrimaryButton>
                        
                        <TertiaryButton
                            onClick={() => {
                                setStep('intro');
                                setBodyState(null);
                                setEmotion(null);
                                setNeed(null);
                                setRegulationChoice(null);
                            }}
                            className="w-full text-[var(--orenda-text-muted)] text-sm"
                        >
                            Start over
                        </TertiaryButton>
                    </PersistentBottomBar>
                )}
            </main>
        </div>
    );
};

export default SoloWhatDoINeed;
